import React, { useState, useEffect } from 'react';
import { InstantSearch, connectHits, connectSearchBox, Configure, Pagination } from 'react-instantsearch-dom';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { Card, Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import { FaTrash, FaSearch } from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/zh-tw';
import '../App.css';
import config from '../routes/config';

moment.locale('zh-tw');

// Add CSS for flashing animation with multi-color text
const flashingStyle = document.createElement('style');
flashingStyle.innerHTML = `
  @keyframes multiColorText {
    0% { color: #ff0000; }  /* Red */
    20% { color: #ff6600; } /* Orange */
    40% { color: #0066cc; } /* Blue */
    60% { color: #000000; } /* Black */
    80% { color: #cc3300; } /* Dark orange-red */
    100% { color: #ff0000; } /* Back to red */
  }
  
  @keyframes iconPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
  
  .flashing-warning {
    font-weight: bold;
  }
  
  .flashing-warning span {
    animation: multiColorText 4s infinite;
  }
  
  .flashing-warning svg {
    animation: iconPulse 1.5s infinite;
    color: red !important;
  }
  
  .warning-container {
    background-color: rgba(255, 240, 240, 0.9) !important;
    border: 1px dashed #ff9999 !important;
    box-shadow: 0 0 8px rgba(255, 0, 0, 0.2);
    transition: all 0.3s ease;
  }
  
  .warning-container:hover {
    box-shadow: 0 0 12px rgba(255, 0, 0, 0.3);
  }
`;
document.head.appendChild(flashingStyle);

// Custom Hit component to display search results with highlights and image
const Hit = ({ hit, onImageClick, warningTypes, infoTypes, projectName }) => {
    const handleDelete = (e) => {
        e.stopPropagation(); // Prevent event bubbling
        const collectionName = projectName || hit._index || 'default';

        console.log(`Attempting to delete document ${hit.id} from collection ${collectionName}`);

        fetch(`${config.typesenseProtocol}://${config.typesenseHost}/collections/${collectionName}/documents/${hit.id}`, {
            method: 'DELETE',
            headers: {
                'X-TYPESENSE-API-KEY': config.typesenseApiKey,
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json().catch(() => {
                    // If response is not JSON, just return a success object
                    return { success: true };
                });
            })
            .then(data => {
                console.log('Delete successful:', data);
                window.location.reload();
            })
            .catch(error => {
                console.error('Error deleting document:', error);
                alert('Failed to delete item. See console for details.');
            });
    };

    const isDangerous = hit.warning === true;

    // Determine if any warning conditions are met
    const hasWarnings = Object.keys(warningTypes).some(key => {
        if (key === 'dangerous_environment') {
            return hit[key] && hit[key] !== "無" && hit[key] !== "none";
        }
        return hit[key] === true;
    });

    // Render warning indicators
    const renderWarnings = () => {
        const warnings = [];

        Object.entries(warningTypes).forEach(([key, config]) => {
            if (key === 'dangerous_environment') {
                if (hit[key] && hit[key] !== "無" && hit[key] !== "none") {
                    const { icon: Icon, color } = config;
                    warnings.push(
                        <span key={key} className="d-flex align-items-center gap-2 flashing-warning">
                            <Icon style={{ color, fontSize: '1rem' }} /> <span style={{ color }}>{hit[key]}</span>
                        </span>
                    );
                }
            } else if (hit[key]) {
                const { icon: Icon, label, color } = config;
                warnings.push(
                    <span key={key} className="d-flex align-items-center gap-2 flashing-warning">
                        <Icon style={{ color, fontSize: '1rem' }} /> <span style={{ color }}>{label}</span>
                    </span>
                );
            }
        });

        return warnings;
    };

    // Render info items
    const renderInfoItems = () => {
        return Object.entries(infoTypes).map(([key, config]) => {
            const value = hit[key];

            // Skip if there's a condition and it's not met
            if (config.condition) {
                const conditionMet = typeof config.condition === 'function'
                    ? config.condition(value, hit)
                    : config.condition;
                if (!conditionMet) return null;
            }

            // Format the value if a formatter is provided
            const displayValue = config.format ? config.format(value) : value;

            const Icon = config.icon;

            return (
                <span key={key} className="d-flex align-items-center gap-2">
                    <Icon style={{ color: config.color, fontSize: '1rem', flexShrink: 0 }} />
                    <span style={{ fontSize: '1rem', lineHeight: '1.2' }}>{displayValue}</span>
                </span>
            );
        }).filter(Boolean); // Remove null entries
    };

    return (
        <Col xs={12} sm={6} md={4} lg={3} xl={2} xxl={1} className="mb-4" style={{ minWidth: '300px' }}>
            <Card className="h-100 position-relative" style={{
                border: hasWarnings ? '2px solid #ff8800' : '2px solid #808080',
                borderRadius: '5px',
                backgroundColor: isDangerous ? '#ffebcc' : '#fff',
                boxShadow: hasWarnings ? '0 0 8px rgba(255, 136, 0, 0.4)' : 'none'
            }}>
                {hit.thumbnail_url && (
                    <Card.Img
                        variant="top"
                        src={hit.thumbnail_url}
                        alt={hit.description}
                        style={{ maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => onImageClick(hit.image_url)}
                    />
                )}
                <Card.Body>
                    <Card.Title className="text-primary">{hit.name}</Card.Title>
                    <Card.Text style={{ fontSize: '0.75rem' }}>{hit.description}</Card.Text>

                    {/* Warning indicators in a dedicated row at the top - only show if warnings exist */}
                    {hasWarnings && (
                        <div
                            className="warning-container"
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '5px',
                                marginBottom: '10px',
                                padding: '5px',
                                backgroundColor: '#fff8e6',
                                borderRadius: '4px',
                                border: '1px dashed #ffcc80'
                            }}>
                            {renderWarnings()}
                        </div>
                    )}

                    {/* Regular information after warnings */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {renderInfoItems()}
                    </div>
                </Card.Body>
                <button
                    onClick={handleDelete}
                    style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <FaTrash style={{ color: 'grey' }} />
                </button>
            </Card>
        </Col>
    );
};

const CustomHits = ({ hits, onImageClick, warningTypes, infoTypes, projectName }) => (
    <Row className="mt-4 hits-container">
        {hits.map(hit => (
            <Hit
                key={hit.objectID}
                hit={hit}
                onImageClick={onImageClick}
                warningTypes={warningTypes}
                infoTypes={infoTypes}
                projectName={projectName}
            />
        ))}
    </Row>
);

const ConnectedHits = connectHits(({ hits, onImageClick, warningTypes, infoTypes, projectName }) => (
    <CustomHits
        hits={hits}
        onImageClick={onImageClick}
        warningTypes={warningTypes}
        infoTypes={infoTypes}
        projectName={projectName}
    />
));

const CustomSearchBox = ({ currentRefinement, refine }) => (
    <Form onSubmit={e => e.preventDefault()}>
        <InputGroup className="mb-3">
            <Form.Control
                type="text"
                placeholder="Search..."
                aria-label="Search"
                value={currentRefinement}
                onChange={e => refine(e.currentTarget.value)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        window.location.reload();  // Reload the page when 'Enter' is pressed
                    }
                }}
            />
            <Button variant="outline-secondary" onClick={() => refine(currentRefinement)}>
                <FaSearch />
            </Button>
        </InputGroup>
    </Form>
);

const ConnectedSearchBox = connectSearchBox(CustomSearchBox);

function CommonSearch({ projectName, projectDisplayName, warningTypes, infoTypes }) {
    const [showModal, setShowModal] = useState(false);
    const [modalImage, setModalImage] = useState('');
    const [filters, setFilters] = useState({
        warning: false
    });

    // Initialize filters based on warning types
    useEffect(() => {
        const initialFilters = { warning: false };
        Object.keys(warningTypes).forEach(key => {
            initialFilters[key] = false;
        });
        setFilters(initialFilters);
    }, [warningTypes]);

    const handleImageClick = (imageUrl) => {
        setModalImage(imageUrl);
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleFilterChange = (e) => {
        const { name, checked } = e.target;

        if (checked) {
            // If checked, set only the selected filter to true, others to false
            const newFilters = { ...filters };
            Object.keys(newFilters).forEach(key => {
                newFilters[key] = false;
            });
            newFilters[name] = true;
            setFilters(newFilters);
        } else {
            // If unchecked, reset all filters to false
            const newFilters = { ...filters };
            Object.keys(newFilters).forEach(key => {
                newFilters[key] = false;
            });
            setFilters(newFilters);
        }
    };

    const buildFilterString = () => {
        const activeFilters = Object.keys(filters).filter(filter => filters[filter] === true);
        if (activeFilters.length === 0) return ''; // If no filters are selected, return an empty string

        return activeFilters
            .map(filter => `${filter}:true`)
            .join(' AND ');
    };

    // Setup Typesense adapter
    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
        server: {
            apiKey: config.typesenseApiKey,
            nodes: [
                {
                    host: config.typesenseHost,
                    port: config.typesensePort,
                    protocol: config.typesenseProtocol,
                },
            ],
        },
        additionalSearchParameters: {
            query_by: 'embedding, description, name',
            vector_query: "embedding:([], alpha: 0.8)",
            hitsPerPage: 1,
            prefix: 'false',
            exclude_fields: 'embedding',
        }
    });

    const searchClient = typesenseInstantsearchAdapter.searchClient;

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                window.location.reload();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <div className="container-fluid mt-4 text-center">
            <h1>【 {projectDisplayName} 】AI事件驅動管理</h1>
            <p className="text-center text-danger small">請按Enter更新資料</p>

            <Form.Group className="custom-checkbox mb-3 d-flex justify-content-center gap-3">
                {Object.entries(filters).map(([key, value]) => {
                    // Skip rendering if the warning type doesn't exist
                    if (key !== 'warning' && !warningTypes[key]) return null;

                    return (
                        <Form.Check
                            key={key}
                            type="checkbox"
                            label={<span style={{ color: '#007bff' }}>
                                {key === 'warning' ? '只顯示警告' :
                                    warningTypes[key]?.label || key}
                            </span>}
                            name={key}
                            checked={value}
                            onChange={handleFilterChange}
                            className="custom-checkbox-item"
                        />
                    );
                })}
            </Form.Group>

            <InstantSearch searchClient={searchClient} indexName={projectName}>
                <Configure
                    hitsPerPage={20}
                    filters={buildFilterString()}
                />
                <ConnectedSearchBox />
                <ConnectedHits
                    onImageClick={handleImageClick}
                    warningTypes={warningTypes}
                    infoTypes={infoTypes}
                    projectName={projectName}
                />
                {/* Add Pagination Component */}
                <Pagination showLast={true} />
            </InstantSearch>

            {showModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                    }}
                    onClick={handleCloseModal}
                >
                    <div
                        style={{
                            position: 'relative',
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '5px',
                            maxWidth: '90%',
                            maxHeight: '90%',
                            minWidth: '40%',
                            overflow: 'auto',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={handleCloseModal}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: 'black',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 2001
                            }}
                        >
                            ✕
                        </button>
                        <div style={{
                            marginTop: '20px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <img
                                src={modalImage}
                                alt="Enlarged view"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 'calc(90vh - 80px)',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CommonSearch; 